import api from '../api/axios';
import AIService, { AITextResponse, AITTSResponse } from './ai.service';

export interface WhatsAppMessage {
  id?: number;
  contact?: any;
  phoneNumber: string;
  message: string;
  messageType?: 'text' | 'image' | 'video' | 'document' | 'audio' | 'template' | 'button';
  direction: 'inbound' | 'outbound';
  status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  messageId?: string;
  mediaUrl?: string;
  errorMessage?: string;
  metadata?: any;
  sentBy?: any;
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
}

export interface AIWhatsAppResponse {
  textResponse: AITextResponse;
  audioResponse?: AITTSResponse;
  success: boolean;
  error?: string;
}

const WhatsAppService = {
  // Messages
  getAllMessages: (params?: any) => api.get('/whatsapp-messages', { params }),
  getMessage: (id: number) => api.get(`/whatsapp-messages/${id}`, { params: { populate: '*' } }),
  
  // Save message to database only (no WhatsApp API call)
  saveMessage: (data: Partial<WhatsAppMessage> & { templateName?: string; templateParams?: any }) => 
    api.post('/whatsapp-messages', { data }),
  
  // Send message via WhatsApp API and save to database
  sendMessage: (data: Partial<WhatsAppMessage> & { templateName?: string; templateParams?: any }) => 
    api.post('/whatsapp-messages/send', { data }),
  
  // Bulk messaging
  sendBulkMessage: (phoneNumbers: string[], message: string, messageType?: string) =>
    api.post('/whatsapp-messages/bulk', { phoneNumbers, message, messageType }),
  
  // Message by contact
  getContactMessages: (contactId: number) =>
    api.get('/whatsapp-messages', {
      params: {
        'filters[contact][id][$eq]': contactId,
        'sort[0]': 'createdAt:desc',
        populate: '*',
      },
    }),
  
  // Get contacts with WhatsApp messages
  getWhatsAppContacts: () => api.get('/whatsapp-messages/contacts'),
  
  // Templates
  getTemplates: () => api.get('/whatsapp-messages/templates'),
  createTemplate: (data: any) => api.post('/whatsapp-messages/templates', { data }),
  deleteTemplate: (templateId: string) => api.delete(`/whatsapp-messages/templates/${templateId}`),
  
  // Campaigns
  sendCampaign: (data: {
    phoneNumbers: string[];
    message?: string;
    templateName?: string;
    templateParams?: any;
    messageType?: string;
    campaignName?: string;
    scheduleAt?: string;
  }) => api.post('/whatsapp-messages/bulk', { data }),
  
  // Analytics & Reports
  getAnalytics: (params?: { startDate?: string; endDate?: string }) => 
    api.get('/whatsapp-messages/analytics', { params }),
  getPhoneNumberStatus: () => api.get('/whatsapp-messages/phone-status'),
  
  // Stats
  getStats: () => api.get('/whatsapp-messages/stats'),

  // AI Integration
  processWithAI: async (
    contactId: number,
    phoneNumber: string,
    incomingMessage: string,
    generateVoice: boolean = false
  ): Promise<AIWhatsAppResponse> => {
    try {
      const userId = `whatsapp_${phoneNumber.replace(/[^0-9]/g, '')}`;
      
      console.log('🤖 Processing with AI:', { userId, message: incomingMessage });
      
      // Call the text processing AI service
      const aiResponse = await AIService.processWhatsAppMessage(
        userId,
        incomingMessage,
        generateVoice
      );

      const msgText = aiResponse.textResponse?.msg 
        ? (typeof aiResponse.textResponse.msg === 'number' 
            ? String(aiResponse.textResponse.msg) 
            : aiResponse.textResponse.msg)
        : '';
      console.log('✅ AI Response:', msgText.substring(0, 100));

      // Save AI response and send via WhatsApp API using /send endpoint
      await api.post('/whatsapp-messages/send', {
        data: {
          contact: contactId,
          phoneNumber,
          message: aiResponse.textResponse.msg,
          messageType: 'text',
          direction: 'outbound',
        }
      });

      return {
        textResponse: aiResponse.textResponse,
        audioResponse: aiResponse.audioResponse,
        success: true,
      };
    } catch (error: any) {
      console.error('AI WhatsApp Processing Error:', error);
      return {
        textResponse: {
          id: `whatsapp_${phoneNumber.replace(/[^0-9]/g, '')}`,
          msg: '❌ عذراً، حدث خطأ في معالجة الرسالة. يرجى المحاولة مرة أخرى.',
        },
        success: false,
        error: error.message || 'Unknown error occurred',
      };
    }
  },

  /**
   * Send AI-generated response to WhatsApp
   * @param contactId - Contact ID
   * @param phoneNumber - Phone number
   * @param message - Message to send
   * @param audioBase64 - Optional audio data
   * @returns Promise<void>
   */
  sendAIResponse: async (
    contactId: number,
    phoneNumber: string,
    message: string,
    audioBase64?: string
  ): Promise<void> => {
    try {
      // Send text message
      await WhatsAppService.sendMessage({
        contact: contactId,
        phoneNumber,
        message,
        messageType: 'text',
        direction: 'outbound',
        status: 'sent',
      });

      // Send audio if available
      if (audioBase64) {
        // Note: You'll need to implement audio sending based on your WhatsApp API
        // This is a placeholder for audio message sending
        console.log('Audio response available:', audioBase64.substring(0, 50) + '...');
      }
    } catch (error: any) {
      console.error('Error sending AI response:', error);
      throw error;
    }
  },

  /**
   * Check AI services health
   * @returns Promise<object>
   */
  checkAIServices: () => AIService.checkServicesHealth(),

  /**
   * Start client flow by sending select_language template
   * @param phoneNumber - Phone number to start flow for
   * @returns Promise<any>
   */
  startClientFlow: (phoneNumber: string) => 
    api.post('/whatsapp-messages/start-client-flow', {
      data: { phoneNumber }
    }),
};

export default WhatsAppService;

